import { useEffect, useState } from 'react';
import { useRouter } from '@/hooks/use-router';
import { usePageHeader } from '@/providers/page-header/use-page-header';
import { usePermissions } from '@/services/auth';
import { BreadcrumbTrail } from './breadcrumbs';
import { Button } from './button';
import { CommandPalette } from './command-palette';
import { Icon } from './icon';
import { SidebarTrigger } from './sidebar';
import { ThemeToggle } from './toggle-theme';

export const AppTopbar = () => {
  const { header } = usePageHeader();
  const { push } = useRouter();
  const { hasPermission } = usePermissions();
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  return (
    <header className="h-16 w-full sticky top-0 z-50 flex items-center justify-between gap-4 border-b border-border bg-background/80 px-4 backdrop-blur-md md:px-6">
      <div className="flex min-w-0 items-center gap-3">
        <SidebarTrigger />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 truncate">
            {header?.icon && <Icon name={header.icon} className="size-4 text-primary" />}
            <span className="truncate font-display text-[15px] font-bold tracking-[-0.01em]">
              {header?.title ?? 'Hagius'}
            </span>
          </div>
          <BreadcrumbTrail items={header?.items ?? []} />
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          className="hidden items-center gap-2 rounded-[10px] border border-border bg-card px-3 h-9 text-sm text-muted-foreground transition-colors hover:border-border-strong sm:flex"
        >
          <Icon name="search" className="size-4" />
          <span>Buscar pedido, cliente...</span>
          <kbd className="ml-4 rounded border border-border px-1.5 py-0.5 text-[10px] font-mono text-faint">
            ⌘K
          </kbd>
        </button>

        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          className="inline-flex size-9 items-center justify-center rounded-[10px] border border-border-strong text-foreground-2 transition-colors hover:bg-muted sm:hidden"
          aria-label="Buscar"
        >
          <Icon name="search" className="size-4" />
        </button>

        <ThemeToggle variant="icon" />

        <button
          type="button"
          className="relative inline-flex size-9 items-center justify-center rounded-[10px] border border-border-strong text-foreground-2 transition-colors hover:bg-muted"
          aria-label="Notificações"
        >
          <Icon name="bell" className="size-4" />
          <span className="absolute right-2 top-2 size-1.5 rounded-full bg-destructive" />
        </button>

        {header?.action ??
          (hasPermission('sales', 'create') && (
            <Button icon="plus" onClick={() => push('/sales/create')}>
              Novo Pedido
            </Button>
          ))}
      </div>

      <CommandPalette open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
};
